import { TileLayout, TileLayoutItem, TilePosition } from '@progress/kendo-react-layout';
import React, { useMemo, useState } from 'react';
import { MessageBarManagement } from './MessageBarManagement';
import { MaintenancePageManagement } from './MaintenancePageManagement';
import { AdminHelpCenterManagement } from './AdminHelpCenterManagement';
import { ReportIssueManagement } from './ReportIssueManagement';
import { PermissionsManagement } from './PermissionManagement';
import { FieldTranslationsManagement } from './translations/FieldTranslationsManagement';
import { SystemViewManagement } from './SystemViewManagement';
import { ConfigurationsManagement } from './configuration/ConfigurationManagement';
import { StaticTranslationsManagement } from './translations/StaticTranslationsManagement';
import { useConfigurationKeysManagementPermissions } from './hooks/useConfigurationKeysManagementPermissions';
import { GenericSettingsMenu } from '../GenericSettingsMenu';
import {
  useGlobalizationContext,
  useTranslation,
  useUiAdminConfigsContext,
  useGetUserPermissions
} from '@cs-cms/state-management';
import { CpsSettingMenuItem } from '@cs-cms/types';
import { ADMIN_CENTER_MENU_ITEM_TYPE } from '../../types/AdminCenterMenuItemType';

const postitions: TilePosition[] = [
  {
    col: 1,
    colSpan: 1
  },
  {
    col: 2,
    colSpan: 1
  }
];

export interface AdminCenterManagementProps {
  extraMenuItems?: CpsSettingMenuItem[];
  renderExtraBody?: (menuItem: string) => React.ReactNode;
}

export const AdminCenterManagement: React.FC<AdminCenterManagementProps> = ({
  extraMenuItems = [],
  renderExtraBody
}) => {
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>(ADMIN_CENTER_MENU_ITEM_TYPE.MESSAGE_BAR);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { resetPreviewState } = useUiAdminConfigsContext();
  const { data: permissions } = useGetUserPermissions();
  const { canEditAnything: canEditAnyConfigKey } = useConfigurationKeysManagementPermissions();

  React.useEffect(() => {
    resetPreviewState();
  }, [resetPreviewState]);

  const menu: CpsSettingMenuItem[] = useMemo(
    () => [
      ...(canEditAnyConfigKey
        ? [
            {
              text: translate('custom.settings.adminCenter.configurations.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.CONFIGURATIONS
            }
          ]
        : []),

      ...(permissions?.setupMessageBar
        ? [
            {
              text: translate('custom.settings.adminCenter.messageBar.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.MESSAGE_BAR
            }
          ]
        : []),
      ...(permissions?.setupMaintenancePage
        ? [
            {
              text: translate('custom.settings.adminCenter.maintenancePage.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.MAINTENANCE_PAGE
            }
          ]
        : []),
      ...(permissions?.editHelpCenter
        ? [
            {
              text: translate('custom.settings.adminCenter.helpCenter.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.HELP_CENTER
            }
          ]
        : []),
      ...(permissions?.editReportAnIssue
        ? [
            {
              text: translate('custom.settings.adminCenter.reportIssue.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.REPORT_ISSUE
            }
          ]
        : []),
      ...(permissions?.viewAssignedRules
        ? [
            {
              text: translate('custom.settings.adminCenter.permissionManagement.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.PERMISSION_MANAGEMENT
            }
          ]
        : []),
      ...(permissions?.editFieldTranslations
        ? [
            {
              text: translate('custom.settings.adminCenter.translationManagement.fieldTranslations.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.FIELD_TRANSLATIONS
            }
          ]
        : []),
      ...(permissions?.editStaticTranslations
        ? [
            {
              text: translate('custom.settings.adminCenter.translationManagement.staticTranslations.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.STATIC_TRANSLATIONS
            }
          ]
        : []),
      ...(permissions?.manageSystemViews
        ? [
            {
              text: translate('custom.settings.adminCenter.systemViewManagement.title'),
              value: ADMIN_CENTER_MENU_ITEM_TYPE.SYSTEM_VIEW_MANAGEMENT
            }
          ]
        : []),
      ...extraMenuItems
    ],
    [canEditAnyConfigKey, permissions, translate, extraMenuItems]
  );

  React.useEffect(() => {
    if (menu.length === 0) {
      return;
    }

    const isSelectedItemVisible = menu.some(item => item.value === selectedMenuItem);
    if (!isSelectedItemVisible) {
      setSelectedMenuItem(menu[0].value);
    }
  }, [menu, selectedMenuItem]);

  const getActiveBody = (menuItem: string | undefined): React.ReactNode => {
    switch (menuItem) {
      case ADMIN_CENTER_MENU_ITEM_TYPE.CONFIGURATIONS:
        return <ConfigurationsManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.MAINTENANCE_PAGE:
        return <MaintenancePageManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.MESSAGE_BAR:
        return <MessageBarManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.HELP_CENTER:
        return <AdminHelpCenterManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.REPORT_ISSUE:
        return <ReportIssueManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.PERMISSION_MANAGEMENT:
        return <PermissionsManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.FIELD_TRANSLATIONS:
        return <FieldTranslationsManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.STATIC_TRANSLATIONS:
        return <StaticTranslationsManagement />;
      case ADMIN_CENTER_MENU_ITEM_TYPE.SYSTEM_VIEW_MANAGEMENT:
        return <SystemViewManagement />;
      default:
        if (menuItem && renderExtraBody) {
          return renderExtraBody(menuItem);
        }
        return <></>;
    }
  };

  const tiles: TileLayoutItem[] = [
    {
      body: (
        <GenericSettingsMenu<CpsSettingMenuItem, string>
          menu={menu}
          value={selectedMenuItem}
          onSelectedMenuItemChanged={(value: string) => setSelectedMenuItem(value)}
        />
      ),
      className: 'helpCenterTileLayoutItem',
      reorderable: false,
      resizable: false
    },
    {
      body: getActiveBody(selectedMenuItem),
      className: 'helpCenterTileLayoutItem',
      reorderable: false,
      resizable: false
    }
  ];

  return (
    <div className="helpCenter">
      <TileLayout columns={2} positions={postitions} items={tiles} />
    </div>
  );
};
